import { useBranchTimezone } from "../api/use-branch-timezone";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type {
  Coverage,
  HrClinicCertificate,
  LeaveBalance,
  PatternDay,
  Permission,
  QuickFillSources,
  RaiseTimeOff,
  RotaDay,
  RotaWeek,
  SaveException,
  StaffMember,
  TeamCard,
  TimeOffRequest,
  WeeklyPattern,
} from "@dentacore/shared-types";

import { ApiError } from "../api/client";
import { listCertificates } from "../api/hr";
import {
  clearException,
  clearWeek,
  listTeam,
  promoteException,
  publishWeek,
  readPattern,
  readQuickFill,
  readWeeks,
  revertPattern,
  savePattern,
  setException,
  unpublishWeek,
} from "../api/rota";
import { listBranches } from "../api/branches";
import { listStaff } from "../api/staff";
import {
  decideTimeOff,
  listBalances,
  listTimeOff,
  raiseTimeOff,
  readCoverage,
  reopenTimeOff,
} from "../api/time-off";
import { useActiveBranch } from "../api/use-active-branch";
import { across, eachLocation, useShown } from "../shell/locations";
import { useAuth } from "../auth/auth-context";
import { Toast } from "../settings/toast";
import { describe, useWriter } from "../settings/use-writer";
import { ConfirmDialog } from "../ui/confirm-dialog";
import { HelpTip } from "../ui/popovers";
import { DayCard } from "./day-card";
import { DayDialog } from "./day-dialog";
import { EveryWeekDialog } from "./every-week-dialog";
import { PersonPanel } from "./person-panel";
import {
  buildPeople,
  dayAndMonth,
  dayFor,
  dayStateOf,
  daysText,
  localToday,
  longDate,
  onLeaveOn,
  roleLabel,
  shiftText,
} from "./roster";
import styles from "./staff.module.css";
import { ComplianceView } from "./compliance-view";
import { MeView } from "./me-view";
import { TeamView } from "./team-view";
import { BalancesView, RaiseDialog, TimeOffView } from "./time-off-view";
import { isPast, mondayOf } from "./weeks";
import { WEEKDAY_NAMES } from "./weekdays";
import { useCan } from "../auth/use-can";
import { mergeWeeks } from "./merge-weeks";
import { useRingProvider } from "../shell/ring/ring-registry";
import { RING_ICONS } from "../shell/ring/ring-icons";

/**
 * Staff, calm (ST9–ST16, 2026-09-28).
 *
 * **Every role reads this**, unlike Settings: a dentist needs to see their own
 * week without an admin opening it for them (STORY-067).
 *
 * **Schedule is one day at a time.** One card holds the date, who is in as a
 * timeline, and who is off or elsewhere at its foot; clicking somebody opens
 * them beside it in their role's colour, with their week, and every change to
 * it is a dialog. **Time off is ST1 as approved**, with each waiting request
 * saying how much leave its person has left, and the balances behind a link.
 * Nothing the page did before is gone (CS1) — see `DECISIONS.md` for where each
 * control went.
 *
 * **HR's four pages since MS-D3 (2026-10-16)**: Shifts, Requests, Staff management
 * and Me are links in the sidebar's HR group, so the page no longer carries its
 * own tab strip — `page` says which one this is. Staff management keeps two tabs
 * of its own, Team and Compliance, because they are one page in Emile's structure.
 */
export type HrPage = "shifts" | "requests" | "staff" | "me";

const TITLES: Record<HrPage, string> = {
  shifts: "Shifts",
  requests: "Requests",
  staff: "Staff management",
  me: "Me",
};

export function StaffPage({ page }: { page: HrPage }): React.JSX.Element {
  const { busy, toast, dismiss, fail, run } = useWriter();

  // **Publishing is its own permission** (S6, citing S3; STORY-166).
  const { user } = useAuth();
  const can = useCan();
  const isAdmin = can("staff.rota.publish");

  /**
   * **The rota is branch-scoped, so nothing may be fetched before the branch
   * is known.** `useActiveBranch` returns null for "not yet"; firing before
   * then sends a request with no branch, the guard refuses it, and the screen
   * sits on "Reading…" in a way that reads as slowness rather than refusal.
   */
  const branchId = useActiveBranch();
  // The locations ticked in the top bar.
  const places = useShown();

  /**
   * **The day and the person live in the address** (STORY-094, ST9), so both
   * survive a reload and a pasted link lands where it points. An old
   * `?week=` still works: it opens that week, on today if today is in it.
   */
  const [params, setParams] = useSearchParams();
  const timezone = useBranchTimezone();
  const today = localToday(timezone);
  const day = dayOfParams(params, today);
  const weekStart = mondayOf(day);
  const selectedId = params.get("person");

  const go = useCallback(
    (next: { day?: string; person?: string | null }) => {
      setParams(
        (current) => {
          const merged = new URLSearchParams(current);
          if (next.day !== undefined) {
            merged.set("day", next.day);
            merged.delete("week");
          }
          if (next.person === null) merged.delete("person");
          else if (next.person !== undefined) merged.set("person", next.person);
          return merged;
        },
        // Paging days must not fill the back button with every day visited.
        { replace: true },
      );
    },
    [setParams],
  );

  /**
   * **Two tabs, because time off is not about one location.** The rota is a
   * location's day; the queue is the whole clinic's, and `time_off_requests`
   * carries no branch to narrow it by.
   */
  /**
   * **Which tabs a person gets** (HR-D1, HR-D18). Team and Compliance follow
   * their permissions. Me is everybody's own record who cannot see the team's;
   * Time off stays for whoever decides leave or sees everyone's, so a clinic's
   * own role that decides without `hr.team.view` keeps the queue it decides.
   */
  const seesMe = !can("hr.team.view") && can("hr.self");
  const seesTimeOff =
    can("hr.team.view") || can("staff.timeoff.decide") || can("staff.leave.view.any");
  /** HR's Team tab, for whoever may see the team's records (HR-D1, STORY-181c). */
  const seesTeam = can("hr.team.view");
  const [section, setSection] = useState<"me" | "rota" | "timeOff" | "team" | "compliance">(
    page === "me"
      ? "me"
      : page === "requests"
        ? "timeOff"
        : page === "staff"
          ? seesTeam
            ? "team"
            : "compliance"
          : "rota",
  );
  /** HR's Compliance tab (HR-D1, STORY-181d); its waiting count is on the tab itself (HR-D16). */
  const seesCompliance = can("hr.compliance.view");
  const [certificates, setCertificates] = useState<readonly HrClinicCertificate[] | null>(null);

  const reloadCertificates = useCallback(async (signal?: AbortSignal) => {
    setCertificates(await listCertificates(signal));
  }, []);

  useEffect(() => {
    if (!seesCompliance) return;
    const controller = new AbortController();
    reloadCertificates(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      fail(describe(cause, "Could not read the certificates."));
    });
    return () => controller.abort();
  }, [seesCompliance, reloadCertificates, fail]);

  const certificatesWaiting = (certificates ?? []).filter(
    (row) => row.pendingRenewal !== null,
  ).length;
  const [timeOffView, setTimeOffView] = useState<"requests" | "balances">("requests");
  const [asking, setAsking] = useState(false);

  const [staff, setStaff] = useState<readonly StaffMember[] | null>(null);
  const [weeks, setWeeks] = useState<readonly RotaWeek[] | null>(null);
  const [team, setTeam] = useState<readonly TeamCard[] | null>(null);
  const [balances, setBalances] = useState<readonly LeaveBalance[] | null>(null);
  const [timeOff, setTimeOff] = useState<readonly TimeOffRequest[] | null>(null);
  const [pattern, setPattern] = useState<WeeklyPattern | null>(null);
  const [sources, setSources] = useState<QuickFillSources | null>(null);

  /** The day whose dialog is open, and whether *Every week* is. */
  const [changing, setChanging] = useState<RotaDay | null>(null);
  const [everyOpen, setEveryOpen] = useState(false);

  /**
   * A change to a day that has already passed, waiting to be confirmed. Holds
   * the action, so the confirm need not know which edit raised it.
   */
  const [confirmingPast, setConfirmingPast] = useState<{ date: string; run: () => void } | null>(
    null,
  );

  /**
   * The coverage warnings the server has held approvals back with (S9), **per
   * request**, because the warning belongs beside the decision it is about.
   */
  const [coverage, setCoverage] = useState<Record<string, Coverage>>({});

  /** The year the balances are for. This one — a picker is not asked for. */
  const balanceYear = new Date().getUTCFullYear();

  /**
   * **How many locations the clinic has open** (EB3). With one, naming it
   * under every person says nothing; with two or more it answers which
   * dentist works where, which the tester could not see from the list.
   */
  const [openLocations, setOpenLocations] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    listBranches(controller.signal)
      .then((rows) => setOpenLocations(rows.filter((row) => row.isActive).length))
      .catch(() => {
        // Quiet: the names are a hint, and the schedule is whole without them.
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    listStaff(controller.signal)
      .then(setStaff)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setStaff([]);
        fail(describe(cause, "Could not read this clinic's people."));
      });
    return () => controller.abort();
  }, [fail]);

  /**
   * The queue and the balances together. **The balances are re-read after
   * every decision**, because approving is exactly what moves one.
   */
  const reloadTimeOff = useCallback(
    async (signal?: AbortSignal) => {
      const [queue, sums] = await Promise.all([
        places.many
          ? // A request asked at two locations is still one request.
            across(places.ids, () => listTimeOff("ALL", signal)).then((rows) => [
              ...new Map(rows.map((row) => [row.id, row])).values(),
            ])
          : listTimeOff("ALL", signal),
        listBalances(undefined, signal),
      ]);
      setTimeOff(queue);
      setBalances(sums);
    },
    [places.key],
  );

  useEffect(() => {
    const controller = new AbortController();
    reloadTimeOff(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      fail(describe(cause, "Could not read the time-off queue."));
    });
    return () => controller.abort();
  }, [reloadTimeOff, fail]);

  /** Everybody's week at this location, and the roster's figures. */
  const reloadWeeks = useCallback(
    async (signal?: AbortSignal) => {
      if (!places.many) {
        const [dated, roster] = await Promise.all([readWeeks(weekStart, signal), listTeam(signal)]);
        setWeeks(dated);
        setTeam(roster);
        return;
      }
      // **One row per person, with every day they work at any location shown**
      // (OQ-D17): each day is taken from the location that has them in, tagged
      // with its name, and the week's paid hours are added up. Someone working
      // Mon–Wed at one and Thu–Fri at another had lost Thu–Fri.
      const [dated, roster] = await Promise.all([
        eachLocation(places.ids, () => readWeeks(weekStart, signal)),
        eachLocation(places.ids, () => listTeam(signal)),
      ]);
      const merged = mergeWeeks(
        dated.map((weeks, index) => ({
          name: places.byId.get(places.ids[index] ?? "")?.name,
          weeks,
        })),
      );
      const cardOf = new Map<string, (typeof roster)[number][number]>();
      for (const list of roster)
        for (const card of list) if (!cardOf.has(card.userId)) cardOf.set(card.userId, card);
      setWeeks(merged);
      setTeam([...cardOf.values()]);
    },
    [weekStart, places.key],
  );

  useEffect(() => {
    if (branchId === null) return;
    const controller = new AbortController();
    setWeeks(null);
    reloadWeeks(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      fail(describe(cause, "Could not read this week for everybody."));
    });
    return () => controller.abort();
  }, [branchId, reloadWeeks, fail]);

  /**
   * The selected person's regular week and what it can be filled from.
   *
   * **Only the week is worth an error.** The fill row is a convenience, so a
   * source that does not answer leaves it out rather than blocking the week.
   */
  const reloadPerson = useCallback(
    async (signal?: AbortSignal) => {
      if (selectedId === null) return;
      const [theirs, fills] = await Promise.all([
        readPattern(selectedId, signal),
        readQuickFill(selectedId, signal).catch(() => null),
      ]);
      setPattern(theirs);
      setSources(fills);
    },
    [selectedId],
  );

  useEffect(() => {
    setPattern(null);
    setSources(null);
    if (selectedId === null || branchId === null) return;
    const controller = new AbortController();
    // Somebody who does not work here has no week to read, and their panel
    // says so; a refused read is not worth a toast.
    reloadPerson(controller.signal).catch(() => undefined);
    return () => controller.abort();
  }, [selectedId, branchId, reloadPerson]);

  /**
   * **A write refreshes the day too.** Changing somebody's Wednesday moves
   * Wednesday's count and their bar; saving a regular week moves their hours —
   * a screen that re-read only the person would contradict itself.
   */
  const reloadAll = useCallback(async () => {
    await Promise.all([reloadPerson(), reloadWeeks(), reloadTimeOff()]);
  }, [reloadPerson, reloadWeeks, reloadTimeOff]);

  const people = useMemo(
    () => buildPeople(staff ?? [], weeks, team, balances, branchId),
    [staff, weeks, team, balances, branchId],
  );
  const selected = people.find((person) => person.id === selectedId) ?? null;

  /** Approved leave — it outranks the rota on the day, the counts and the week. */
  const approved = useMemo(
    () => (timeOff ?? []).filter((row) => row.status === "APPROVED"),
    [timeOff],
  );

  function saveWeek(days: PatternDay[], effectiveFrom: string): void {
    if (selectedId === null) return;
    void run(() => savePattern(selectedId, { effectiveFrom, days }), reloadAll, "Week saved.").then(
      (ok) => {
        if (ok) setEveryOpen(false);
      },
    );
  }

  function revert(): void {
    if (selectedId === null) return;
    void run(() => revertPattern(selectedId), reloadAll, "Back to the previous week.").then(
      (ok) => {
        if (ok) setEveryOpen(false);
      },
    );
  }

  /**
   * Change one day, **asking first when it has already happened** (Anthony's
   * QA, item 5): a past day stays recordable — a sick day nobody logged is
   * otherwise permanently wrong — but never changes without resistance.
   */
  function guardPast(date: string, apply: () => void): void {
    if (isPast(date)) setConfirmingPast({ date, run: apply });
    else apply();
  }

  function closeOnSuccess(done: Promise<boolean>): void {
    void done.then((ok) => {
      if (ok) setChanging(null);
    });
  }

  function saveDay(date: string, body: SaveException): void {
    if (selectedId === null) return;
    guardPast(date, () =>
      closeOnSuccess(
        run(() => setException(selectedId, date, body), reloadAll, "That day is set."),
      ),
    );
  }

  function clearDay(date: string): void {
    if (selectedId === null) return;
    guardPast(date, () =>
      closeOnSuccess(
        run(() => clearException(selectedId, date), reloadAll, "Back to the regular hours."),
      ),
    );
  }

  function promoteDay(date: string): void {
    if (selectedId === null) return;
    closeOnSuccess(
      run(
        () => promoteException(selectedId, date),
        reloadAll,
        "That is the regular day from this week on.",
      ),
    );
  }

  /** This week's changes undone — the panel's person, or the one the ring was opened on. */
  function clearThisWeek(userId: string | null = selectedId): void {
    if (userId === null) return;
    void run(
      () => clearWeek(userId, weekStart),
      reloadAll,
      "This week is back to the regular hours.",
    );
  }

  function raise(body: RaiseTimeOff): void {
    void run(() => raiseTimeOff(body), reloadTimeOff, "Asked. It is waiting on a decision.").then(
      (ok) => {
        if (ok) setAsking(false);
      },
    );
  }

  /**
   * Approve or decline — and catch the coverage warning rather than shouting
   * it. **A held-back approval is not a failure**: the row draws the report
   * and *Approve anyway* sends the same call with `acknowledge`. The report is
   * fetched rather than read off the error, since `ApiError` carries no body.
   */
  function decide(id: string, what: "approve" | "decline", acknowledge?: boolean): void {
    setCoverage((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });

    decideTimeOff(id, what, acknowledge === true ? { acknowledge: true } : {})
      .then(() => reloadTimeOff())
      .catch((cause: unknown) => {
        if (cause instanceof ApiError && cause.status === 409) {
          readCoverage(id)
            .then((held) => setCoverage((current) => ({ ...current, [id]: held })))
            .catch(() =>
              fail("Approving this would leave work behind, but the details did not load."),
            );
          return;
        }
        fail(describe(cause, "That decision did not go through."));
      });
  }

  function reopen(id: string): void {
    void run(() => reopenTimeOff(id), reloadTimeOff, "Back in the queue.");
  }

  const publication = weeks?.[0]?.publication ?? null;

  function publish(): void {
    void run(() => publishWeek(weekStart), reloadAll, "The week is published.");
  }

  /** What the body actually draws — the same fall-through as the render below. */
  const view =
    section === "me" && seesMe
      ? "me"
      : section === "compliance" && seesCompliance
        ? "compliance"
        : section === "team" && seesTeam
          ? "team"
          : section === "timeOff" && seesTimeOff
            ? "timeOff"
            : "rota";

  /*
    ── The quick ring (QR7–QR9, STORY-221) ──────────────────────────────────
    Each action below is a control this page already draws, run through the
    same state or handler that control uses (QR8). Nothing here is new.
  */

  // Blank space on Shifts or Requests: the page's own ring.
  useRingProvider("page", () => {
    if (view !== "rota" && view !== "timeOff") return null;
    const waiting = (timeOff ?? []).filter((row) => row.status === "PENDING").length;
    return {
      ring: "page.hr",
      title: TITLES[page],
      meta: view === "rota" ? longDate(day) : `${String(waiting)} waiting`,
      actions: [
        // The title row's *Ask for time off* — the dialog is the page's, on either view.
        !busy && {
          key: "ask",
          label: "Ask for time off",
          icon: RING_ICONS.day,
          hint: "A leave request",
          run: () => setAsking(true),
        },
        // The week's ⋯ → *Publish this week*, offered while the week is a draft.
        view === "rota" &&
          !busy &&
          publication !== null &&
          !publication.published && {
            key: "pub",
            label: "Publish this week",
            icon: RING_ICONS.send,
            needs: ["staff.rota.publish"],
            hint: "The team sees the rota",
            run: publish,
          },
      ],
    };
  });

  // A person on the day's timeline.
  useRingProvider("hr.person", (element) => {
    const person = people.find((row) => row.id === element.dataset.ringId);
    if (person === undefined) return null;
    const row = dayFor(person, day);
    // The panel's rule (QA third run): your own week needs `.own`, anyone else's `.any` too.
    const edit: Permission[] =
      person.id === user?.id
        ? ["staff.rota.edit.own"]
        : ["staff.rota.edit.own", "staff.rota.edit.any"];
    const writable = person.active && person.week !== null;
    const onLeave =
      row !== null && dayStateOf(row, onLeaveOn(person.id, day, approved)) === "leave";
    return {
      ring: "hr.person",
      title: person.name,
      meta:
        row === null
          ? roleLabel(person.role)
          : `${roleLabel(person.role)} · ${onLeave ? "On leave" : shiftText(row)}`,
      subject: { type: "staff", id: person.id },
      actions: [
        // A day in their week → the day dialog, for the day on the card.
        writable &&
          row !== null && {
            key: "today",
            label: `Change ${WEEKDAY_NAMES[row.dayOfWeek] ?? "this day"}`,
            icon: RING_ICONS.day,
            needs: edit,
            hint: "Only this day",
            run: () => {
              go({ person: person.id });
              setChanging(row);
            },
          },
        // *Edit every week…* at the panel's foot.
        writable && {
          key: "week",
          label: "Edit every week",
          icon: RING_ICONS.week,
          needs: edit,
          hint: "Their regular hours",
          run: () => {
            go({ person: person.id });
            setEveryOpen(true);
          },
        },
        // The panel's ⋯ → *Clear this week's changes*, only while there are some.
        writable &&
          !busy &&
          (person.week?.exceptionCount ?? 0) > 0 && {
            key: "clear",
            label: "Clear changes",
            icon: RING_ICONS.undo,
            needs: edit,
            hint: "Back to their regular week",
            run: () => clearThisWeek(person.id),
          },
      ],
    };
  });

  // A time-off request. **The row says whether you may decide it** (S8): nobody decides their own (QF-D6).
  useRingProvider("hr.request", (element) => {
    const request = (timeOff ?? []).find((row) => row.id === element.dataset.ringId);
    if (request === undefined) return null;
    const dates =
      request.startsOn === request.endsOn
        ? dayAndMonth(request.startsOn)
        : `${dayAndMonth(request.startsOn)} – ${dayAndMonth(request.endsOn)}`;
    const balance = (balances ?? []).find((row) => row.userId === request.userId);
    const left =
      request.status !== "PENDING"
        ? request.status === "APPROVED"
          ? "Approved"
          : "Declined"
        : balance === undefined || balance.remainingDays === null
          ? "No leave allowance set"
          : `${daysText(balance.remainingDays)} of ${daysText(balance.entitlementDays ?? 0)} days left`;
    const pending = request.status === "PENDING";
    return {
      ring: "hr.request",
      title: request.name,
      meta: `${dates} · ${left}`,
      subject: { type: "timeoff", id: request.id },
      actions: [
        pending &&
          request.canDecide &&
          !busy && {
            key: "approve",
            label: "Approve",
            icon: RING_ICONS.check,
            needs: ["staff.timeoff.decide"],
            hint: dates,
            run: () => decide(request.id, "approve"),
          },
        pending &&
          request.canDecide &&
          !busy && {
            key: "decline",
            label: "Decline",
            icon: RING_ICONS.close,
            needs: ["staff.timeoff.decide"],
            danger: true,
            hint: dates,
            run: () => decide(request.id, "decline"),
          },
        // A decided row's *Reopen*.
        !pending &&
          !busy && {
            key: "reopen",
            label: "Reopen",
            icon: RING_ICONS.undo,
            needs: ["staff.timeoff.decide"],
            hint: "Back in the queue",
            run: () => reopen(request.id),
          },
      ],
    };
  });

  return (
    <div className={styles.page} data-testid="staff-page">
      <header className={styles.head}>
        {/* The lead paragraph behind a ?, its wording kept (CS11). */}
        <h1 className={styles.title}>{TITLES[page]}</h1>
        <HelpTip label="About the staff screen">
          Each person&rsquo;s repeating week at this location, with their paid hours against what
          they are contracted for.
        </HelpTip>

        {section === "timeOff" && (
          <span className={styles.headActions}>
            <button
              type="button"
              className={styles.quiet}
              aria-pressed={timeOffView === "balances"}
              onClick={() =>
                setTimeOffView((was) => (was === "balances" ? "requests" : "balances"))
              }
              data-testid="balances-toggle"
            >
              {timeOffView === "balances" ? "Back to the requests" : "Leave balances"}
            </button>
            <button
              type="button"
              className={styles.primary}
              disabled={busy}
              onClick={() => setAsking(true)}
              data-testid="raise-open"
            >
              Ask for time off
            </button>
          </span>
        )}
      </header>

      {/* Only Staff management has tabs left: Team and Compliance (MS-D3). */}
      {page === "staff" && seesTeam && seesCompliance && (
        <div className={styles.tabs} role="tablist" aria-label="Team or compliance">
          {seesTeam && (
            <button
              type="button"
              role="tab"
              aria-selected={section === "team"}
              className={section === "team" ? `${styles.tab} ${styles.tabOn}` : styles.tab}
              onClick={() => setSection("team")}
              data-testid="section-team"
            >
              Team
            </button>
          )}
          {seesCompliance && (
            <button
              type="button"
              role="tab"
              aria-selected={section === "compliance"}
              className={section === "compliance" ? `${styles.tab} ${styles.tabOn}` : styles.tab}
              onClick={() => {
                setSection("compliance");
                // Team may have renewed something since; the list is read fresh.
                reloadCertificates().catch(() => undefined);
              }}
              data-testid="section-compliance"
            >
              Compliance
              {certificatesWaiting > 0 && (
                <span className={styles.tabWarm} data-testid="compliance-waiting-count">
                  {certificatesWaiting} waiting
                </span>
              )}
            </button>
          )}
        </div>
      )}

      {section === "me" && seesMe ? (
        <div className={styles.body}>
          <MeView writer={{ busy, run, fail }} />
        </div>
      ) : section === "compliance" && seesCompliance ? (
        <div className={styles.body}>
          <ComplianceView
            certificates={certificates}
            people={(staff ?? [])
              .filter((row) => row.isActive)
              .map((row) => ({ userId: row.id, name: `${row.firstName} ${row.lastName}` }))
              .sort((a, b) => a.name.localeCompare(b.name))}
            canManage={can("hr.compliance.manage")}
            writer={{ busy, run, fail }}
            reload={() => reloadCertificates()}
          />
        </div>
      ) : section === "team" && seesTeam ? (
        <div className={styles.body}>
          <TeamView
            meId={user?.id ?? null}
            currency={user?.currency ?? "USD"}
            can={can}
            writer={{ busy, run, fail }}
          />
        </div>
      ) : section === "timeOff" && seesTimeOff ? (
        <div className={styles.body}>
          {timeOffView === "balances" ? (
            <div className={styles.toColumn}>
              <BalancesView balances={balances} year={balanceYear} />
            </div>
          ) : (
            <TimeOffView
              requests={timeOff}
              balances={balances}
              busy={busy}
              onDecide={decide}
              onReopen={reopen}
              coverage={coverage}
            />
          )}
        </div>
      ) : (
        <div className={styles.body}>
          <DayCard
            day={day}
            today={today}
            people={people}
            approved={approved}
            loading={weeks === null || staff === null}
            publication={publication}
            exceptionTotal={(weeks ?? []).reduce((sum, row) => sum + row.exceptionCount, 0)}
            isAdmin={isAdmin}
            busy={busy}
            selectedId={selectedId}
            showLocations={openLocations >= 2}
            onDay={(next) => go({ day: next })}
            onSelect={(id) => go({ person: id === selectedId ? null : id })}
            onPublish={publish}
            onUnpublish={() => {
              void run(() => unpublishWeek(weekStart), reloadAll, "The week is back to draft.");
            }}
          />

          {selected !== null && (
            <PersonPanel
              person={selected}
              pattern={pattern}
              day={day}
              today={today}
              approved={approved}
              loading={weeks === null || staff === null}
              busy={busy}
              onClose={() => go({ person: null })}
              onChangeDay={setChanging}
              onEveryWeek={() => setEveryOpen(true)}
              onClearWeek={() => clearThisWeek()}
            />
          )}
        </div>
      )}

      {changing !== null && selected !== null && (
        <DayDialog
          day={changing}
          name={selected.name}
          usual={
            // Only this person's week: the ring can open the dialog before theirs has arrived.
            pattern?.userId === selected.id
              ? pattern.days.find((row) => row.dayOfWeek === changing.dayOfWeek)
              : undefined
          }
          busy={busy}
          onSave={(body) => saveDay(changing.date, body)}
          onClear={() => clearDay(changing.date)}
          onPromote={() => promoteDay(changing.date)}
          onClose={() => setChanging(null)}
        />
      )}

      {everyOpen && selected !== null && pattern !== null && pattern.userId === selected.id && (
        <EveryWeekDialog
          person={selected}
          pattern={pattern}
          sources={sources}
          busy={busy}
          onSave={saveWeek}
          onRevert={revert}
          onError={fail}
          onClose={() => setEveryOpen(false)}
        />
      )}

      {asking && (
        <RaiseDialog
          people={staff}
          meId={user?.id ?? null}
          busy={busy}
          onRaise={raise}
          onClose={() => setAsking(false)}
        />
      )}

      {confirmingPast !== null && (
        <ConfirmDialog
          title="That day has already passed"
          confirmLabel="Record it anyway"
          busy={busy}
          onCancel={() => setConfirmingPast(null)}
          onConfirm={() => {
            const { run: apply } = confirmingPast;
            setConfirmingPast(null);
            apply();
          }}
          testId="rota-past-day-confirm"
        >
          <p>{longDate(confirmingPast.date)} is behind us. Change it anyway?</p>
          <p>
            Worth doing for a sick day or a swap nobody logged at the time &mdash; leave balances
            are counted from these days, so an unrecorded one stays wrong. Worth cancelling if you
            meant a day still to come.
          </p>
        </ConfirmDialog>
      )}

      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </div>
  );
}

/**
 * The day named in the address.
 *
 * **A hand-edited address degrades rather than breaks.** `?day=` wins when it
 * is a date; an old `?week=` opens that week — on today when today is in it,
 * on its Monday otherwise — and anything else is today.
 */
function dayOfParams(params: URLSearchParams, today: string): string {
  const valid = (value: string | null): value is string =>
    value !== null &&
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    !Number.isNaN(Date.parse(`${value}T00:00:00.000Z`));

  const dayParam = params.get("day");
  if (valid(dayParam)) return dayParam;

  const weekParam = params.get("week");
  if (valid(weekParam)) {
    const monday = mondayOf(weekParam);
    return mondayOf(today) === monday ? today : monday;
  }
  return today;
}
